import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Settings, User, LogOut, Menu, ChevronRight, Folder as FolderIcon, Sun, Moon, Monitor } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAppStore } from '@/stores/appStore'
import { logout } from '@/services/auth'
import { api, Folder } from '@/services/api'
import { useTheme } from '@/lib/theme'
import { LogoMark } from './Logo'

function ThemeToggle() {
  const { theme, cycle } = useTheme()
  const Icon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Monitor
  const label = theme === 'system' ? 'Theme: system' : theme === 'dark' ? 'Theme: dark' : 'Theme: light'
  return (
    <Button variant="ghost" size="icon" onClick={cycle} title={label} aria-label={label} className="h-8 w-8 text-muted-foreground">
      <Icon className="h-4 w-4" />
    </Button>
  )
}

function FolderTree() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentOrg, folderContents, isLoading } = useAppStore()

  const orgId = currentOrg?.id || null
  const activeFolderId = searchParams.get('folder')

  const rootFolder = folderContents.path?.[0] || null

  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [childrenById, setChildrenById] = useState<Record<string, Folder[]>>({})
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!rootFolder) return
    setExpanded((prev) => {
      if (prev.has(rootFolder.id)) return prev
      const next = new Set(prev)
      next.add(rootFolder.id)
      return next
    })
  }, [rootFolder])

  const loadChildren = useCallback(
    async (folderId: string) => {
      if (!orgId) return
      if (childrenById[folderId]) return
      if (loadingIds.has(folderId)) return

      setLoadingIds((prev) => {
        const next = new Set(prev)
        next.add(folderId)
        return next
      })

      try {
        const res = await api.folders.contents(orgId, folderId)
        setChildrenById((prev) => ({ ...prev, [folderId]: res.folders || [] }))
      } catch {
        setChildrenById((prev) => ({ ...prev, [folderId]: [] }))
      } finally {
        setLoadingIds((prev) => {
          const next = new Set(prev)
          next.delete(folderId)
          return next
        })
      }
    },
    [childrenById, loadingIds, orgId]
  )

  useEffect(() => {
    if (!rootFolder) return
    void loadChildren(rootFolder.id)
  }, [rootFolder, loadChildren])

  const onNavigateToFolder = useCallback(
    (folderId: string) => {
      const next = new URLSearchParams(searchParams)
      next.set('folder', folderId)
      setSearchParams(next)
    },
    [searchParams, setSearchParams]
  )

  const toggleExpand = useCallback(
    async (folderId: string) => {
      const willExpand = !expanded.has(folderId)
      setExpanded((prev) => {
        const next = new Set(prev)
        if (next.has(folderId)) next.delete(folderId)
        else next.add(folderId)
        return next
      })
      if (willExpand) {
        await loadChildren(folderId)
      }
    },
    [expanded, loadChildren]
  )

  const sortedChildrenById = useMemo(() => {
    const next: Record<string, Folder[]> = {}
    for (const [k, v] of Object.entries(childrenById)) {
      next[k] = [...v].sort((a, b) => a.name.localeCompare(b.name))
    }
    return next
  }, [childrenById])

  if (!orgId || !rootFolder) {
    return (
      <div className="px-4 py-3 text-sm text-muted-foreground">
        {isLoading ? 'Loading…' : 'Select a location to browse folders.'}
      </div>
    )
  }

  const renderNode = (folder: Folder, depth: number) => {
    const isExpanded = expanded.has(folder.id)
    const kids = sortedChildrenById[folder.id] || []
    const isActive = !!activeFolderId && activeFolderId === folder.id
    const hasLoadedChildren = (sortedChildrenById[folder.id]?.length ?? 0) > 0

    return (
      <div key={folder.id}>
        <div
          className={
            `group flex items-center gap-1 rounded-md py-1.5 pr-2 text-sm transition-colors ` +
            (isActive
              ? 'bg-primary/10 text-primary font-medium'
              : 'text-foreground/70 hover:bg-accent hover:text-foreground')
          }
          style={{ paddingLeft: 6 + depth * 14 }}
        >
          <button
            type="button"
            className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              void toggleExpand(folder.id)
            }}
            aria-label={isExpanded ? 'Collapse folder' : 'Expand folder'}
          >
            <ChevronRight className={`h-3.5 w-3.5 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''} ${!isExpanded && !hasLoadedChildren ? 'opacity-50' : ''}`} />
          </button>

          <FolderIcon className={`h-4 w-4 shrink-0 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />

          <button
            type="button"
            className="flex-1 truncate text-left"
            title={folder.name}
            onClick={() => onNavigateToFolder(folder.id)}
          >
            {folder.name}
          </button>
        </div>

        {isExpanded && (
          <div>
            {loadingIds.has(folder.id) && (
              <div className="px-2 py-1 text-xs text-muted-foreground" style={{ paddingLeft: 12 + (depth + 1) * 14 }}>
                Loading…
              </div>
            )}
            {kids.map((child) => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    )
  }

  return <div className="overflow-auto px-2 py-2">{renderNode(rootFolder, 0)}</div>
}

export function Layout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAppStore()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const showTree = location.pathname.startsWith('/files')

  const handleLogout = () => {
    logout().finally(() => navigate('/login'))
  }

  const closeSidebar = useCallback(() => setSidebarOpen(false), [])

  return (
    <div className="flex h-screen bg-background">
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-foreground/40 backdrop-blur-sm lg:hidden"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-64 transform flex-col border-r bg-card transition-transform duration-200 lg:static lg:flex lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-label="Folder navigation"
      >
        <div className="flex items-center gap-2.5 px-4 py-3.5">
          <LogoMark size={28} />
          <button
            type="button"
            onClick={() => navigate('/files')}
            className="text-[15px] font-semibold tracking-tight text-foreground transition-colors hover:text-primary"
          >
            Freehold
          </button>
        </div>

        <div className="flex-1 overflow-hidden border-t">
          {showTree ? <FolderTree /> : null}
        </div>

        <div className="border-t p-2.5">
          <div className="flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-auto min-w-0 flex-1 justify-start gap-2.5 px-2 py-1.5">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/12 text-primary">
                    <span className="text-xs font-semibold">
                      {user?.display_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || '?'}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1 text-left">
                    <p className="truncate text-sm font-medium leading-tight">{user?.display_name || 'User'}</p>
                    <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                {user?.is_admin && (
                  <DropdownMenuItem onClick={() => navigate('/settings')}>
                    <Settings className="mr-2 h-4 w-4" />
                    Admin settings
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <ThemeToggle />
          </div>
        </div>
      </aside>

      <main className="relative flex-1 overflow-auto">
        <div className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b bg-background/80 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/65 lg:hidden">
          <button
            type="button"
            onClick={() => setSidebarOpen((o) => !o)}
            className="rounded-md border bg-card p-2 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Toggle folder navigation"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2 font-semibold">
            <LogoMark size={28} />
            Freehold
          </div>
        </div>

        <Outlet />
      </main>
    </div>
  )
}
